import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Progress,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { useReceivePurchaseOrder } from './purchasingApi';
import type { PurchaseOrderDto } from './types';

interface Props {
  po: PurchaseOrderDto | null;
  opened: boolean;
  onClose: () => void;
}

// Operator enters "received THIS round" per line — server adds to the
// running ReceivedQuantity. Zero-skip: empty fields don't get a delta.
export function ReceivePurchaseOrderModal({ po, opened, onClose }: Props) {
  const { t } = useTranslation();
  const receive = useReceivePurchaseOrder(po?.purchaseOrderId ?? '');
  const [deltas, setDeltas] = useState<Record<string, number | ''>>({});
  const [batches, setBatches] = useState<Record<string, string>>({});
  const [expiries, setExpiries] = useState<Record<string, Date | null>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setDeltas({});
      setBatches({});
      setExpiries({});
      setError(null);
    }
  }, [opened, po]);

  if (!po) return null;

  const submit = async () => {
    setError(null);
    const entries = Object.entries(deltas)
      .map(([id, v]) => ({
        purchaseOrderLineId: id,
        receivedDelta: Number(v) || 0,
        batchNumber: batches[id]?.trim() || null,
        expiryDate: expiries[id] ? toDateOnlyString(expiries[id]!) : null,
      }))
      .filter((e) => e.receivedDelta > 0);
    if (entries.length === 0) {
      setError(t('purchasing.receive.empty'));
      return;
    }
    try {
      await receive.mutateAsync({ lines: entries });
      notifications.show({
        color: 'green',
        title: t('purchasing.receive.success'),
        message: po.displayNumber,
      });
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail ?? (err instanceof Error ? err.message : t('purchasing.receive.failed')));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      title={<Text fw={600}>{t('purchasing.receive.title')} — {po.displayNumber}</Text>}
    >
      <Stack gap="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('purchasing.receive.item')}</Table.Th>
              <Table.Th className="text-end">{t('purchasing.receive.ordered')}</Table.Th>
              <Table.Th className="text-end">{t('purchasing.receive.alreadyReceived')}</Table.Th>
              <Table.Th style={{ width: 130 }}>{t('purchasing.receive.receivingNow')}</Table.Th>
              <Table.Th style={{ width: 140 }}>{t('purchasing.receive.batchLabel')}</Table.Th>
              <Table.Th style={{ width: 150 }}>{t('purchasing.receive.expiryLabel')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {po.lines.map((line) => {
              const remaining = line.quantity - line.receivedQuantity;
              const progress = (line.receivedQuantity / line.quantity) * 100;
              const lineId = line.purchaseOrderLineId;
              return (
                <Table.Tr key={lineId}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={500}>{line.itemNameAr}</Text>
                      <Text size="xs" c="dimmed" className="font-mono">{line.itemSku}</Text>
                      <Progress value={progress} color={progress >= 100 ? 'green' : 'wheels'} size="xs" />
                    </Stack>
                  </Table.Td>
                  <Table.Td className="font-numeric text-end">{line.quantity}</Table.Td>
                  <Table.Td className="font-numeric text-end">{line.receivedQuantity}</Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={deltas[lineId] ?? ''}
                      onChange={(v) =>
                        setDeltas((prev) => ({
                          ...prev,
                          [lineId]: typeof v === 'string' ? Number(v) : v,
                        }))
                      }
                      min={0}
                      max={remaining}
                      disabled={remaining <= 0}
                      decimalScale={3}
                      placeholder={remaining > 0 ? String(remaining) : '—'}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      value={batches[lineId] ?? ''}
                      onChange={(e) =>
                        setBatches((prev) => ({ ...prev, [lineId]: e.currentTarget.value }))
                      }
                      disabled={remaining <= 0}
                      placeholder={t('purchasing.receive.batchPlaceholder')}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <DatePickerInput
                      value={expiries[lineId] ?? null}
                      onChange={(v) =>
                        setExpiries((prev) => ({ ...prev, [lineId]: v }))
                      }
                      disabled={remaining <= 0}
                      clearable
                      minDate={new Date()}
                      size="xs"
                    />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} stroke={1.6} />}>{error}</Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={receive.isPending}>
            {t('common.cancel')}
          </Button>
          <Button color="wheels" onClick={submit} loading={receive.isPending}>
            {t('purchasing.receive.apply')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function toDateOnlyString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
